import React, {
  createContext, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { io } from 'socket.io-client';
import { UserContext } from '../User';
import { SocketUrl } from '../../config';

/*
 * One Socket.IO connection per signed-in session.
 *
 * The server has served sockets to the desktop agent for a long time; the web app
 * simply never connected. It reuses the same `io` instance and the per-user room
 * (`notify-<userId>`) every connection already joins — see server/utils/io.js.
 *
 * Identity comes from the access token, which the server verifies on the handshake
 * (server/utils/socketAuth.js). Nothing is claimed in the query string: an unverified
 * userId there is exactly the hole that was closed to make this safe for the web.
 *
 * The socket is a live-update channel, never a source of truth. Anything it delivers
 * is also readable from the REST API, so a dropped connection costs freshness, not
 * data — consumers refetch on reconnect.
 */
/*
 * How long to wait before retrying a handshake the server refused for an expired
 * token, and how many times. Sized to outlast a token refresh triggered by the next
 * REST call rather than to poll: the socket is an optimisation, and the Notification
 * Center reads the same rows over HTTP either way.
 */
const EXPIRED_TOKEN_RETRY_MS = 5000;
const EXPIRED_TOKEN_RETRIES = 5;

export const SocketContext = createContext({ socket: null, connected: false });

export const useSocket = () => useContext(SocketContext);

export function SocketProvider({ children }) {
  const { accessToken, member } = useContext(UserContext);
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);

  /*
   * The token is read at connect time but deliberately NOT a dependency: it is
   * refreshed on a timer, and reconnecting on every refresh would drop and rebuild a
   * perfectly good connection (losing events in the gap). A workspace switch changes
   * `member`, which is what genuinely needs a new connection.
   */
  const tokenRef = useRef(accessToken);
  tokenRef.current = accessToken;

  const workspaceId = member?.workspaceId;

  useEffect(() => {
    if (!tokenRef.current || !workspaceId) {
      setSocket(null);
      setConnected(false);
      return undefined;
    }

    let expiredRetries = 0;
    let retryTimer = null;

    const instance = io(SocketUrl, {
      /*
       * A callback, not a plain object: socket.io re-invokes it on every reconnection
       * attempt, whereas an object is captured once at construction. The access token
       * expires after 24 hours (auth/jwt.js), so a tab left open overnight reconnects
       * with a token the server has to reject — and since this effect is deliberately
       * not keyed on the token, nothing would ever rebuild the connection with the
       * fresh one the REST layer had already fetched.
       */
      auth: (cb) => cb({ token: tokenRef.current }),
      // Allow the polling fallback: some corporate proxies block the websocket
      // upgrade, and a notification bell is not worth losing to that.
      transports: ['websocket', 'polling'],
      /*
       * Listing polling second is NOT on its own a fallback. engine.io only moves to
       * the next transport in the list on a failed open when this flag is set
       * (engine.io-client's Socket._onError); left off — its default — a websocket the
       * network or the server refuses ends the attempt, and the polling entry above
       * never gets used. That is the difference between degrading to polling and
       * showing the user a bell that never connects.
       */
      tryAllTransports: true,
      withCredentials: true,
    });

    instance.on('connect', () => {
      expiredRetries = 0;
      setConnected(true);
    });
    instance.on('disconnect', () => setConnected(false));

    /*
     * Logged once per connection rather than per attempt: reconnection backs off but
     * never gives up, so logging every retry buries the first failure — the only one
     * that says why. A handshake the server refuses outright (an origin missing from
     * config.socketAllowedOrigins, say) surfaces here as a transport error with no
     * `data.code`, and without this it is invisible apart from the browser's own
     * "WebSocket connection failed", which does not name a cause.
     */
    let reported = false;

    instance.on('connect_error', (error) => {
      setConnected(false);
      const code = error?.data?.code;

      if (!reported) {
        reported = true;
        console.warn(
          `[socket] connection to ${SocketUrl} failed: ${error?.message || error}`,
          code ? `(${code})` : '(transport or handshake rejected)',
        );
      }

      // No code means the transport never reached the server's middleware — a refused
      // upgrade, a dropped network. socket.io's own reconnection covers that case.
      if (!code) return;

      /*
       * Past here the server answered and turned us away, and socket.io does NOT
       * reconnect after a middleware rejection — recovery has to be asked for.
       *
       * Only an expired token is worth asking for: `auth` above re-reads the token on
       * each attempt, so once the REST layer has refreshed it the next try succeeds.
       * Bounded, because a refresh that never lands would otherwise spin forever; when
       * the attempts run out the user still has the REST layer's 401 handling.
       */
      if (code === 'token_expired' && expiredRetries < EXPIRED_TOKEN_RETRIES) {
        expiredRetries += 1;
        retryTimer = setTimeout(() => instance.connect(), EXPIRED_TOKEN_RETRY_MS);
        return;
      }

      // Anything else (no token, a forged or revoked one) fails identically forever.
      instance.disconnect();
    });

    setSocket(instance);

    return () => {
      clearTimeout(retryTimer);
      instance.removeAllListeners();
      instance.disconnect();
    };
  }, [workspaceId]);

  const value = useMemo(() => ({ socket, connected }), [socket, connected]);

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}

SocketProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
